import { FC } from 'react';
import { SummaryStatCard } from 'components/summary-stat-card/summary-stat-card';
import { formatNumber } from 'utils/number';
import type { CountyCoverageSummaryResponse } from 'types/api-responses';
import styles from './county-coverage.module.scss';

export type CountyCoverageCardType =
  'last24h' | 'last7d' | 'last30d' | 'last6m';

interface SummaryCardsProps {
  stats: CountyCoverageSummaryResponse | undefined;
  onCardClick?: (card: CountyCoverageCardType) => void;
}

export const SummaryCards: FC<SummaryCardsProps> = ({ stats, onCardClick }) => {
  const top3StatesCount =
    stats?.topStates
      ?.slice(0, 3)
      .reduce((sum, state) => sum + state.documentCount, 0) || 0;

  const top3CountiesCount =
    stats?.topCounties
      ?.slice(0, 3)
      .reduce((sum, county) => sum + county.documentCount, 0) || 0;

  return (
    <div className={styles.cardsContainer}>
      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Total Documents"
          value={formatNumber(
            stats?.totalDocuments,
            0,
            0,
            'decimal',
            true,
            '--'
          )}
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Last 24 Hours"
          value={
            stats?.last24Hours !== undefined
              ? `+${formatNumber(
                  stats.last24Hours,
                  0,
                  0,
                  'decimal',
                  true,
                  '--'
                )}`
              : '--'
          }
          valueColor="green"
          onFilterClick={onCardClick ? () => onCardClick('last24h') : undefined}
          filterTooltip="Filter by Last 24 Hours"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Last 7 Days"
          value={
            stats?.last7Days !== undefined
              ? `+${formatNumber(stats.last7Days, 0, 0, 'decimal', true, '--')}`
              : '--'
          }
          valueColor="green"
          onFilterClick={onCardClick ? () => onCardClick('last7d') : undefined}
          filterTooltip="Filter by Last 7 Days"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Last Month"
          value={
            stats?.last30Days !== undefined
              ? `+${formatNumber(
                  stats.last30Days,
                  0,
                  0,
                  'decimal',
                  true,
                  '--'
                )}`
              : '--'
          }
          valueColor="green"
          onFilterClick={onCardClick ? () => onCardClick('last30d') : undefined}
          filterTooltip="Filter by Last Month"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Last 6 Months"
          value={
            stats?.last6Months !== undefined
              ? `+${formatNumber(
                  stats.last6Months,
                  0,
                  0,
                  'decimal',
                  true,
                  '--'
                )}`
              : '--'
          }
          valueColor="green"
          onFilterClick={onCardClick ? () => onCardClick('last6m') : undefined}
          filterTooltip="Filter by Last 6 Months"
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Top 3 States"
          value={
            top3StatesCount > 0
              ? `${formatNumber(top3StatesCount, 0, 0, 'decimal', true, '--')}`
              : '--'
          }
        />
      </div>

      <div className={styles.cardCell}>
        <SummaryStatCard
          title="Top 3 Counties"
          value={
            top3CountiesCount > 0
              ? `${formatNumber(
                  top3CountiesCount,
                  0,
                  0,
                  'decimal',
                  true,
                  '--'
                )}`
              : '--'
          }
        />
      </div>
    </div>
  );
};
